/**
 * Scroll-Spy using IntersectionObserver
 * - Watches menu section headings
 * - Highlights the active category tab
 * - Lock flag prevents observer from fighting with programmatic smooth scroll
 */

let scrollLockUntil = 0;
const LOCK_DURATION_MS = 900;

let observer: IntersectionObserver | null = null;
let onActiveChange: ((catId: string) => void) | null = null;

function getStickyHeaderOffset(): number {
  const hasServiceTabs = typeof document !== 'undefined' && Boolean(document.querySelector('.nav-service-tab'));
  // 56px header + 45px service tabs (if present) + 42px category tabs
  return 56 + (hasServiceTabs ? 45 : 0) + 42;
}

/**
 * Initialize IntersectionObserver on all `.menu-section` elements.
 * The observer root margin accounts for the sticky header (~56px/101px) + sticky tabs (~42px).
 */
export function initScrollSpy(callback: (catId: string) => void): void {
  destroy();
  onActiveChange = callback;

  const offset = getStickyHeaderOffset();

  observer = new IntersectionObserver(
    (entries) => {
      if (Date.now() < scrollLockUntil) return;

      // Find the topmost visible section
      let topEntry: IntersectionObserverEntry | null = null;
      for (const entry of entries) {
        if (entry.isIntersecting) {
          if (!topEntry || entry.boundingClientRect.top < topEntry.boundingClientRect.top) {
            topEntry = entry;
          }
        }
      }

      if (topEntry) {
        const catId = (topEntry.target as HTMLElement).dataset['category'];
        if (catId) onActiveChange?.(catId);
      }
    },
    {
      // Trigger when a section header crosses just below the sticky tabs
      rootMargin: `-${offset}px 0px -60% 0px`,
      threshold: 0,
    }
  );

  const sections = document.querySelectorAll<HTMLElement>('.menu-section');
  sections.forEach((el) => observer!.observe(el));

  // Edge case: if scrolled to very bottom, activate last category
  window.addEventListener('scroll', handleBottomEdge, { passive: true });
}

function handleBottomEdge(): void {
  if (Date.now() < scrollLockUntil) return;
  const atBottom =
    window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 20;
  if (atBottom) {
    const sections = document.querySelectorAll<HTMLElement>('.menu-section');
    const last = sections[sections.length - 1];
    if (last?.dataset['category']) {
      onActiveChange?.(last.dataset['category']);
    }
  }
}

/**
 * Smooth-scroll to a category section and lock the observer temporarily
 * to prevent tab flickering while scrolling.
 */
export function scrollToCategory(catId: string): void {
  const target = document.getElementById(`menu-section-${catId}`);
  if (!target) return;

  scrollLockUntil = Date.now() + LOCK_DURATION_MS;

  const headerHeight = getStickyHeaderOffset();
  const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - 8;

  window.scrollTo({ top, behavior: 'smooth' });
}

/**
 * Auto-scroll the category tab strip so the active tab is centred.
 */
export function centerActiveTab(catId: string): void {
  const strip = document.getElementById('cat-tabs-strip');
  const tab = document.getElementById(`cat-tab-${catId}`);
  if (!strip || !tab) return;
  const offset = tab.offsetLeft - strip.clientWidth / 2 + tab.clientWidth / 2;
  strip.scrollTo({ left: offset, behavior: 'smooth' });
}

/** Tear down the observer and event listener. */
export function destroy(): void {
  observer?.disconnect();
  observer = null;
  onActiveChange = null;
  window.removeEventListener('scroll', handleBottomEdge);
}
